// src/components/auth/SignupModal.tsx
import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/authService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import logoImageWhite from '../../assets/images/UI_orifoli_logo_white.png';
import poweredByFigryne from "@/assets/images/UI_powered_by_figryne_white.png";

interface SignupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  onSwitchToLogin?: () => void;
}

const SignupModal: React.FC<SignupModalProps> = ({ isOpen, onClose, onSuccess, onSwitchToLogin }) => {
  const { t } = useTranslation('auth');
  const { user, signInWithGoogle, signInWithTwitter } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(0);
  const [socialLoginProvider, setSocialLoginProvider] = useState<string | null>(null);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [pendingSocialLogin, setPendingSocialLogin] = useState(false);

  // 新規ユーザーを/settingsに送る（invite等からの場合はstate付き）
  const navigateToSettings = () => {
    onClose();
    const stored = sessionStorage.getItem('afterAuthAction');
    const parsed = stored ? JSON.parse(stored) : null;
    navigate('/settings', parsed ? {
      state: {
        from: { pathname: parsed.returnTo },
        openWizardAfterSettings: true,
        defaultCategory: parsed.defaultCategory,
      },
    } : undefined);
  };

  // ソーシャルログイン成功時にプロフィール存在チェック
  useEffect(() => {
    const checkUserProfile = async () => {
      if (user && isOpen && pendingSocialLogin) {
        try {
          const userDocRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userDocRef);

          if (!userDoc.exists()) {
            navigateToSettings();
          } else {
            const userData = userDoc.data();
            // 表示名がない場合もプロフィール設定ページへ
            if (!userData.displayName) {
              navigateToSettings();
            } else {
              // 既存ユーザー: 通常のフロー
              onSuccess?.();
              onClose();
            }
          }
        } catch (error) {
          console.error('Error checking user profile:', error);
          onSuccess?.();
          onClose();
        }
        setPendingSocialLogin(false);
      }
    };

    checkUserProfile();
  }, [user, isOpen, pendingSocialLogin, onClose, onSuccess, navigate]);

  // モーダルが開いたときにbodyのスクロールを無効化
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      setEmail('');
      setIsCodeSent(false);
      setVerificationCode('');
      setError(null);
      setCountdown(0);
      setIsLoading(false);
      setSocialLoginProvider(null);
      setShowEmailForm(false);
      setPendingSocialLogin(false);
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // カウントダウンタイマー
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [countdown]);

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    if (!email) {
      setError(t('errors.emailRequired'));
      setIsLoading(false);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError(t('errors.emailInvalid'));
      setIsLoading(false);
      return;
    }

    try {
      await authService.sendVerificationCode(email, 'ja');
      setIsCodeSent(true);
      setError(null);
      setVerificationCode('');
      setCountdown(60);
    } catch (error: any) {
      let errorMessage = t('errors.codeSendFailed');
      if (error.code === 'functions/invalid-argument') {
        errorMessage = t('errors.emailInvalidFormat');
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    if (!verificationCode || verificationCode.length !== 6) {
      setError(t('errors.codeRequired'));
      setIsLoading(false);
      return;
    }

    try {
      const result = await authService.verifyCode(email, verificationCode);
      if (result.success) {
        if (result.isNewUser) {
          navigateToSettings();
        } else {
          // 既存ユーザー: displayNameがあるかチェック
          if (result.userId) {
            const userDocRef = doc(db, 'users', result.userId);
            const userDoc = await getDoc(userDocRef);
            if (!userDoc.exists() || !userDoc.data()?.displayName) {
              navigateToSettings();
              return;
            }
          }
          // displayNameがある場合: 通常のフロー
          onSuccess?.();
          onClose();
        }
      } else {
        throw new Error(t('errors.authFailed'));
      }
    } catch (error: any) {
      let errorMessage = t('errors.codeVerifyFailed');
      if (error.code === 'functions/invalid-argument') {
        errorMessage = t('errors.codeInvalid');
      } else if (error.code === 'functions/not-found') {
        errorMessage = t('errors.codeNotFound');
      } else if (error.code === 'functions/deadline-exceeded') {
        errorMessage = t('errors.codeExpired');
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    value = value.replace(/["'""'']/g, '').trim();
    value = value.replace(/[^0-9]/g, '');
    const truncatedValue = value.slice(0, 6);
    setVerificationCode(truncatedValue);
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setIsLoading(true);
    setSocialLoginProvider('google');
    setPendingSocialLogin(true);

    try {
      await signInWithGoogle();
    } catch (error: any) {
      console.error('Google signup error:', error);
      let errorMessage = t('errors.googleSignupFailed');
      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = t('errors.signupCancelled');
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        errorMessage = t('errors.accountExistsWithDifferentCredential');
      }
      setError(errorMessage);
      setIsLoading(false);
      setSocialLoginProvider(null);
      setPendingSocialLogin(false);
    }
  };

  const handleTwitterSignIn = async () => {
    setError(null);
    setIsLoading(true);
    setSocialLoginProvider('twitter');
    setPendingSocialLogin(true);

    try {
      await signInWithTwitter();
    } catch (error: any) {
      console.error('X signup error:', error);
      let errorMessage = t('errors.xSignupFailed');
      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = t('errors.signupCancelled');
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        errorMessage = t('errors.accountExistsWithDifferentCredential');
      }
      setError(errorMessage);
      setIsLoading(false);
      setSocialLoginProvider(null);
      setPendingSocialLogin(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* 背景のオーバーレイ（ぼかし効果付き） */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* モーダル本体 */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 py-8 sm:py-4 overflow-y-auto"
            onClick={onClose}
          >
            <motion.div
              className="w-full max-w-md my-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {/* ロゴ */}
              <div className="mb-4 flex justify-between items-center">
                <div className="flex items-center cursor-pointer" onClick={onClose}>
                  <img src={logoImageWhite} alt="ORIFOLI" className="h-8" />
                </div>
                <img src={poweredByFigryne} alt="Powered by Figryne" className="h-7 mr-1.5" />
              </div>

              <div
                className="bg-white rounded-xl shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                {/* 閉じるボタン */}
                <button
                  onClick={onClose}
                  className="absolute right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
                  style={{ top: 'max(1rem, env(safe-area-inset-top, 1rem))' }}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>

                {/* エラーメッセージ */}
                {error && (
                  <div className="p-3 text-sm bg-red-500 bg-opacity-10 text-red-700">
                    {error}
                  </div>
                )}

                <div className="p-6">
                  {/* タイトル */}
                  <h3 className="text-xl font-bold mb-6 text-center text-gray-900">
                    {t('signup.title')}
                  </h3>

                  {/* ソーシャルログインボタン */}
                  {!isCodeSent && !showEmailForm && (
                    <>
                      {/* SNSログイン説明文 */}
                      <div className="mb-6">
                        <p className="text-sm text-gray-800 text-left mb-1">
                          {t('signup.description')}
                        </p>
                        <p className="text-xs text-gray-600 text-left">
                          {t('signup.hint')}
                        </p>
                      </div>

                      <div className="space-y-4 mb-4">
                        <button
                          onClick={handleGoogleSignIn}
                          className={`w-full relative py-3 px-4 bg-white border border-gray-300 text-gray-800 rounded-2xl font-medium ${
                            isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
                          }`}
                          disabled={isLoading}
                        >
                          <div className="absolute left-4 top-1/2 -translate-y-1/2">
                            <svg className="w-5 h-5" viewBox="0 0 24 24">
                              <path
                                fill="#4285F4"
                                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                              />
                              <path
                                fill="#34A853"
                                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                              />
                              <path
                                fill="#FBBC05"
                                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                              />
                              <path
                                fill="#EA4335"
                                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                              />
                            </svg>
                          </div>
                          <div className="text-center">
                            {socialLoginProvider === 'google' && isLoading ? (
                              <span className="flex items-center justify-center">
                                <svg
                                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-800"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  ></circle>
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  ></path>
                                </svg>
                                {t('signup.registering')}
                              </span>
                            ) : (
                              t('signup.withGoogle')
                            )}
                          </div>
                        </button>

                        <button
                          onClick={handleTwitterSignIn}
                          className={`w-full relative py-3 px-4 bg-black text-white rounded-2xl font-medium ${
                            isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-800'
                          }`}
                          disabled={isLoading}
                        >
                          <div className="absolute left-4 top-1/2 -translate-y-1/2">
                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                            </svg>
                          </div>
                          <div className="text-center">
                            {socialLoginProvider === 'twitter' && isLoading ? (
                              <span className="flex items-center justify-center">
                                <svg
                                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  ></circle>
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  ></path>
                                </svg>
                                {t('signup.registering')}
                              </span>
                            ) : (
                              t('signup.withX')
                            )}
                          </div>
                        </button>
                      </div>

                      {/* 区切り線 */}
                      <div className="my-6 relative">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-gray-300"></div>
                        </div>
                        <div className="relative flex justify-center text-sm">
                          <span className="px-2 bg-white text-gray-500">{t('login.or')}</span>
                        </div>
                      </div>

                      {/* メールアドレスで登録ボタン */}
                      <button
                        onClick={() => setShowEmailForm(true)}
                        className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-gray-50 text-gray-600 rounded-2xl font-medium hover:bg-gray-100 transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        {t('signup.withEmail')}
                      </button>

                      {/* ログインへの切り替え */}
                      {onSwitchToLogin && (
                        <div className="text-center mt-6">
                          <p className="text-gray-600 text-sm">
                            {t('signup.hasAccount')}
                            <button
                              type="button"
                              className="font-medium text-indigo-600 hover:underline"
                              onClick={onSwitchToLogin}
                            >
                              {t('signup.login')}
                            </button>
                          </p>
                        </div>
                      )}
                    </>
                  )}

                  {/* メール認証フォーム（コード入力） */}
                  {isCodeSent && (
                    <form onSubmit={handleVerifyCode}>
                      <div className="mb-6">
                        <label htmlFor="verificationCode" className="block font-bold text-gray-700 mb-2 text-center">
                          {t('verification.title')}
                        </label>
                        <p className="text-gray-500 font-bold text-sm mb-2">
                          {t('verification.description', { email })}
                        </p>
                        <input
                          id="verificationCode"
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={verificationCode}
                          onChange={handleCodeChange}
                          className="text-center w-full px-4 py-3 bg-gray-100 rounded-2xl text-gray-950 focus:outline-none focus:ring-2 focus:ring-indigo-600"
                          placeholder={t('verification.placeholder')}
                          maxLength={6}
                          autoComplete="one-time-code"
                          style={{ letterSpacing: '0.5em' }}
                        />
                      </div>

                      <button
                        type="submit"
                        className={`w-full bg-[#b566ff] text-white py-3 rounded-2xl font-medium ${
                          isLoading || verificationCode.length !== 6
                            ? 'opacity-50 cursor-not-allowed'
                            : 'hover:bg-indigo-700'
                        }`}
                        disabled={isLoading || verificationCode.length !== 6}
                      >
                        {isLoading ? t('verification.verifying') : t('signup.submit')}
                      </button>

                      <div className="mt-4 text-center">
                        {countdown > 0 ? (
                          <p className="text-gray-600">
                            {t('verification.resendIn', { count: countdown })}
                          </p>
                        ) : (
                          <button
                            type="button"
                            onClick={handleSendCode}
                            className="text-indigo-600 hover:underline"
                            disabled={isLoading}
                          >
                            {t('verification.resend')}
                          </button>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          setIsCodeSent(false);
                          setVerificationCode('');
                        }}
                        className="mt-8 w-full text-gray-600 hover:text-gray-800"
                      >
                        {t('email.change')}
                      </button>
                    </form>
                  )}

                  {/* メールアドレス入力フォーム */}
                  {!isCodeSent && showEmailForm && (
                    <>
                      <form onSubmit={handleSendCode}>
                        <div className="mb-6">
                          <label htmlFor="email" className="text-center block text-gray-500 mb-2 font-medium">
                            {t('email.label')}
                          </label>
                          <input
                            id="email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full px-4 py-3 bg-gray-100 rounded-2xl text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-600"
                            placeholder={t('email.placeholder')}
                            disabled={isLoading}
                          />
                        </div>

                        <button
                          type="submit"
                          className={`w-full bg-[#b566ff] text-white py-3 rounded-2xl font-medium ${
                            isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#7835b8]'
                          }`}
                          disabled={isLoading}
                        >
                          {isLoading ? t('verification.sending') : t('verification.sendCode')}
                        </button>
                      </form>

                      {/* 戻るボタン */}
                      <button
                        onClick={() => {
                          setShowEmailForm(false);
                          setError(null);
                        }}
                        className="w-full mt-4 text-gray-500 text-sm hover:text-gray-700 transition-colors"
                      >
                        {t('signup.otherMethods')}
                      </button>

                      {/* ログインへの切り替え */}
                      {onSwitchToLogin && (
                        <div className="text-center mt-6">
                          <p className="text-gray-600 text-sm">
                            {t('signup.hasAccount')}
                            <button
                              type="button"
                              className="font-medium text-indigo-600 hover:underline"
                              onClick={onSwitchToLogin}
                            >
                              {t('signup.login')}
                            </button>
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SignupModal;
